import { gridDisk, gridDistance } from 'h3-js';

// Utilidades compartidas por las capas de pulso viajero (onda.js,
// segundoOrden.js): el mismo tratamiento visual (franja delgada tras un
// frente que avanza, no un disco relleno) aplicado a tiempos de viaje reales
// (isocronas.json, r5py) o, cuando esos datos no estan disponibles, a una
// cascada topologica por anillos H3 (k-ring) como aproximacion estilizada.

export const GL_SRC_ALPHA = 0x0302;
export const GL_ONE = 1;
export const GL_FUNC_ADD = 0x8006;

// Mezcla aditiva: las celdas suman brillo donde se superponen en vez de
// taparse, para que el pulso se lea como energia y no como relleno opaco. Es
// para las ondas y los arcos (haces de energia), no para los marcadores
// discretos: cientos de puntos aditivos en un centro denso saturan a blanco y
// pierden el color por tipo.
export const PARAMETROS_ADITIVOS = {
  blend: true,
  blendFunc: [GL_SRC_ALPHA, GL_ONE],
  blendEquation: GL_FUNC_ADD,
  depthTest: false,
};

// Marcadores discretos (puntos de servicio): mezcla normal (alfa sobre fondo)
// para que cada punto conserve el color de su tipo aunque se amontonen, y
// siempre por encima del mapa.
export const PARAMETROS_MARCADOR = {
  depthTest: false,
};

// Brillo maximo justo en el frente, cae rapido hacia la cola (curva no
// lineal para que el pulso se vea nitido y no como una rampa difusa). null
// si la celda esta fuera de la franja visible (aun no alcanzada, o ya muy
// atras del frente).
export function brilloPulso(trasElFrente, ancho) {
  if (trasElFrente < 0 || trasElFrente > ancho) return null;
  return Math.pow(1 - trasElFrente / ancho, 1.6);
}

// Pesos de catchment del modelo E2SFCA (05-ratings.py CATCHMENT_AREAS): un
// servicio a menos de 10 min pesa completo, entre 10 y 20 pesa 0.42, entre 20
// y 30 casi no aporta. Sirve para que los marcadores encendidos codifiquen el
// decaimiento por distancia que usa el indice: los cercanos brillan mas.
const CATCHMENTS = [
  [0, 10, 1.0],
  [10, 20, 0.42],
  [20, 30, 0.03],
];

export function pesoCatchment(minutos) {
  for (const [desde, hasta, peso] of CATCHMENTS) {
    if (minutos >= desde && minutos < hasta) return peso;
  }
  return minutos < 0 ? 1.0 : 0;
}

function distanciaSegura(origenH3, celda) {
  try {
    return gridDistance(origenH3, celda);
  } catch {
    return null;
  }
}

// Las celdas de una onda son FIJAS por origen: durante la animacion solo
// cambia su brillo. Entregar el array estable (memoizado en App) permite a
// deck.gl teselar los hexagonos una sola vez y actualizar solo el buffer de
// colores por frame; regenerar el data por frame re-teselaba todo y era el
// cuello de botella del escaneo.

// "pares" es isocronas.json[origen]: array [idxDestino, minutos]. "grilla"
// resuelve idxDestino al id H3 (mismo orden que grilla.json).
export function celdasConTiempos(pares, grilla) {
  const celdas = [];
  for (const [idxDestino, minutos] of pares) {
    const fila = grilla[idxDestino];
    if (fila) celdas.push({ h3: fila.h3, minutos });
  }
  return celdas;
}

// Respaldo cuando no hay isocronas reales: cascada topologica (gridDisk +
// gridDistance de h3-js). Estilizada, no un tiempo de viaje real.
export function celdasConAnillos(origenH3, maxAnillos) {
  const celdas = [];
  for (const celda of gridDisk(origenH3, maxAnillos)) {
    const distancia = distanciaSegura(origenH3, celda);
    if (distancia === null || distancia < 0) continue;
    celdas.push({ h3: celda, anillo: distancia });
  }
  return celdas;
}
